export type SignableAction = 'approve' | 'reject';

export type MintArgs = {
  workflowId: string;
  taskNumber: number;
  action: SignableAction;
  comments: string;
};

export type MintedCMS = {
  cms: string;
  payloadDigest: string;
  payloadType: string;
};

export interface Signer {
  sign(args: MintArgs): Promise<MintedCMS>;
}

export const SIGNING_ERROR_CODES = {
  SIGNATURE_REQUIRED: 'SIGNATURE_REQUIRED',
  SIGNATURE_NOT_FOUND: 'SIGNATURE_NOT_FOUND',
  SIGNATURE_NOT_VERIFIED: 'SIGNATURE_NOT_VERIFIED',
  IDENTITY_MISMATCH: 'IDENTITY_MISMATCH',
  PAYLOAD_REBIND_MISMATCH: 'PAYLOAD_REBIND_MISMATCH',
  CERT_TYPE_NOT_ALLOWED: 'CERT_TYPE_NOT_ALLOWED',
  CERT_REVOKED: 'CERT_REVOKED',
  CERT_TRUST_FAILURE: 'CERT_TRUST_FAILURE',
  CHAIN_INVALID: 'CHAIN_INVALID',
  CMS_PARSE_ERROR: 'CMS_PARSE_ERROR',
  DIGEST_MISMATCH: 'DIGEST_MISMATCH',
  SIGNING_UNCONFIGURED: 'SIGNING_UNCONFIGURED',
  BAD_REQUEST: 'BAD_REQUEST',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  UNKNOWN: 'UNKNOWN',
  NO_CARD: 'NO_CARD',
  WRONG_PIN: 'WRONG_PIN',
  PIN_LOCKED: 'PIN_LOCKED',
  KEY_ACCESS_DENIED: 'KEY_ACCESS_DENIED',
  USER_CANCELLED: 'USER_CANCELLED',
} as const;

export type SigningErrorCode =
  (typeof SIGNING_ERROR_CODES)[keyof typeof SIGNING_ERROR_CODES];

const ERROR_MESSAGES: Record<SigningErrorCode, string> = {
  SIGNATURE_REQUIRED: 'This task requires a digital signature.',
  SIGNATURE_NOT_FOUND: 'The signature was not found. Please try again.',
  SIGNATURE_NOT_VERIFIED: 'Signature could not be verified. Please try again.',
  IDENTITY_MISMATCH:
    'Your account is not bound to the signing certificate that produced this signature.',
  PAYLOAD_REBIND_MISMATCH:
    'The signed content differs from the action being taken. Please re-sign.',
  CERT_TYPE_NOT_ALLOWED:
    'This task requires a CAC certificate; the provided certificate is not allowed.',
  CERT_REVOKED: 'The signing certificate has been revoked.',
  CERT_TRUST_FAILURE: 'The signing certificate is not trusted.',
  CHAIN_INVALID: 'The signing certificate chain is invalid.',
  CMS_PARSE_ERROR: 'The signature payload was malformed.',
  DIGEST_MISMATCH: 'The signed digest does not match the action content.',
  SIGNING_UNCONFIGURED:
    'Signing is not available on this server. Contact an administrator.',
  BAD_REQUEST: 'The signing request was invalid.',
  INTERNAL_ERROR: 'An internal error occurred while processing the signature.',
  UNKNOWN: 'An unexpected error occurred.',
  NO_CARD: 'No CAC detected. Insert your card and try again.',
  WRONG_PIN: 'PIN incorrect. Please try again.',
  PIN_LOCKED:
    'PIN locked after too many incorrect attempts. Contact your CAC administrator.',
  KEY_ACCESS_DENIED:
    'Access to the private key was denied. Check the key password and try again.',
  USER_CANCELLED: 'Signing cancelled.',
};

export function describeSigningError(
  code: string | undefined,
  fallback?: string,
): string {
  if (code && code in ERROR_MESSAGES) {
    return ERROR_MESSAGES[code as SigningErrorCode];
  }
  return fallback ?? ERROR_MESSAGES.UNKNOWN;
}
